/**
 * Statistics.tsx - 统计分析页面
 * 展示详细的数据分析、漏斗转化、趋势图表、渠道对比等
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  DatePicker,
  Select,
  Button,
  Space,
  Statistic,
  Table,
  Tabs,
  Typography,
  Spin,
  Empty,
  Tag,
  Tooltip,
  Progress,
} from 'antd';
import {
  DownloadOutlined,
  ReloadOutlined,
  RiseOutlined,
  FallOutlined,
  LineChartOutlined,
  PieChartOutlined,
  FunnelPlotOutlined,
  BarChartOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from '@ant-design/icons';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  BarChart,
  Bar,
  LineChart,
  Line,
  FunnelChart,
  Funnel,
  LabelList,
  ComposedChart,
} from 'recharts';
import { useDailyStats, useChannelStats, useFunnelStats, useDashboardStats } from '../hooks';
import { statsApi } from '../api/client';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;

// 颜色配置
const COLORS = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2'];
const CHANNEL_COLORS: Record<string, string> = {
  email: '#1890ff',
  twitter: '#1DA1F2',
  telegram: '#0088cc',
};

const Statistics: React.FC = () => {
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(30, 'day'),
    dayjs(),
  ]);
  const [selectedChannel, setSelectedChannel] = useState<string>('all');
  const [loading, setLoading] = useState(false);

  // 数据查询
  const { data: dashboardStats } = useDashboardStats();
  const { data: dailyStats, isLoading: dailyLoading } = useDailyStats({
    start_date: dateRange[0].format('YYYY-MM-DD'),
    end_date: dateRange[1].format('YYYY-MM-DD')
  });
  const { data: channelStats, isLoading: channelLoading } = useChannelStats({
    start_date: dateRange[0].format('YYYY-MM-DD'),
    end_date: dateRange[1].format('YYYY-MM-DD')
  });
  const { data: funnelStats, isLoading: funnelLoading } = useFunnelStats();

  // 导出报告 (暂未实现)
  const handleExport = async () => {
    setLoading(true);
    try {
      // TODO: 实现导出功能
      console.log('Export not implemented yet');
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setLoading(false);
    }
  };

  // 计算趋势变化
  const calculateTrend = (current: number, previous: number): { value: number; isUp: boolean } => {
    if (previous === 0) return { value: 0, isUp: true };
    const change = ((current - previous) / previous) * 100;
    return { value: Math.abs(change), isUp: change >= 0 };
  };

  // 准备漏斗数据 - 使用API返回的stages格式，添加value字段用于图表
  const funnelData = (funnelStats?.stages || []).map((stage: any) => ({
    ...stage,
    value: stage.count || 0,
  }));

  // 渠道分布数据 - 将对象格式转换为数组格式用于图表
  const channelDistribution = channelStats
    ? Object.entries(channelStats).map(([channel, data]: [string, any]) => ({
        channel,
        sent: data?.sent || 0,
        delivered: data?.delivered || 0,
        opened: data?.opened || 0,
        clicked: data?.clicked || 0,
        replied: data?.replied || 0,
        replies: data?.replied || 0,
        reply_rate: data?.sent > 0 ? (data?.replied / data?.sent) * 100 : 0,
        interested: 0,
        converted: 0,
        conversion_rate: 0,
      }))
    : [];

  // 每日趋势数据 - dailyStats是数组，需要转换字段名
  const dailyTrendData = (dailyStats || []).map((item: any) => ({
    date: item.date,
    sent: (item.emails_sent || 0) + (item.twitter_dms_sent || 0) + (item.telegram_messages_sent || 0),
    replies: item.replies_received || 0,
    interested: item.meetings_booked || 0,
    reply_rate: item.reply_rate || 0,
  }));

  return (
    <div className="statistics-page">
      {/* 筛选工具栏 */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Space size="large">
              <Space>
                <Text strong>时间范围：</Text>
                <RangePicker
                  value={dateRange}
                  onChange={(dates) => dates && setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs])}
                  presets={[
                    { label: '最近7天', value: [dayjs().subtract(7, 'day'), dayjs()] },
                    { label: '最近30天', value: [dayjs().subtract(30, 'day'), dayjs()] },
                    { label: '最近90天', value: [dayjs().subtract(90, 'day'), dayjs()] },
                    { label: '本月', value: [dayjs().startOf('month'), dayjs()] },
                    { label: '上月', value: [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')] },
                  ]}
                />
              </Space>
              <Space>
                <Text strong>渠道：</Text>
                <Select
                  value={selectedChannel}
                  onChange={setSelectedChannel}
                  style={{ width: 120 }}
                >
                  <Option value="all">全部渠道</Option>
                  <Option value="email">Email</Option>
                  <Option value="twitter">Twitter</Option>
                  <Option value="telegram">Telegram</Option>
                </Select>
              </Space>
            </Space>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleExport}
              loading={loading}
            >
              导出报告
            </Button>
          </Col>
        </Row>
      </Card>

      {/* 核心指标卡片 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="总发送量"
              value={dashboardStats?.period_stats?.total_sent || 0}
              prefix={<LineChartOutlined />}
              suffix={
                <Text type="secondary" style={{ fontSize: 14 }}>
                  {dashboardStats?.period_stats?.sent_trend?.isUp ? (
                    <ArrowUpOutlined style={{ color: '#52c41a' }} />
                  ) : (
                    <ArrowDownOutlined style={{ color: '#f5222d' }} />
                  )}
                  {dashboardStats?.period_stats?.sent_trend?.value?.toFixed(1)}%
                </Text>
              }
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="总回复数"
              value={dashboardStats?.period_stats?.total_replies || 0}
              prefix={<BarChartOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="回复率"
              value={dashboardStats?.period_stats?.reply_rate || 0}
              precision={2}
              suffix="%"
              prefix={<PieChartOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="转化率"
              value={dashboardStats?.period_stats?.conversion_rate || 0}
              precision={2}
              suffix="%"
              prefix={<FunnelPlotOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 图表区域 */}
      <Tabs defaultActiveKey="trend">
        {/* 趋势分析 */}
        <TabPane
          tab={<span><LineChartOutlined /> 趋势分析</span>}
          key="trend"
        >
          <Row gutter={16}>
            <Col span={16}>
              <Card title="发送与回复趋势" loading={dailyLoading}>
                <ResponsiveContainer width="100%" height={400}>
                  <ComposedChart data={dailyTrendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(val) => dayjs(val).format('MM-DD')}
                    />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" />
                    <RechartsTooltip
                      labelFormatter={(val) => dayjs(val).format('YYYY-MM-DD')}
                    />
                    <Legend />
                    <Area
                      yAxisId="left"
                      type="monotone"
                      dataKey="sent"
                      name="发送量"
                      fill="#1890ff"
                      fillOpacity={0.3}
                      stroke="#1890ff"
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="replies"
                      name="回复数"
                      stroke="#52c41a"
                      strokeWidth={2}
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="interested"
                      name="有意向"
                      stroke="#722ed1"
                      strokeWidth={2}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </Card>
            </Col>
            <Col span={8}>
              <Card title="回复率趋势" loading={dailyLoading}>
                <ResponsiveContainer width="100%" height={400}>
                  <AreaChart data={dailyTrendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(val) => dayjs(val).format('MM-DD')}
                    />
                    <YAxis domain={[0, 100]} tickFormatter={(val) => `${val}%`} />
                    <RechartsTooltip
                      labelFormatter={(val) => dayjs(val).format('YYYY-MM-DD')}
                      formatter={(val: number) => [`${val.toFixed(2)}%`, '回复率']}
                    />
                    <Area
                      type="monotone"
                      dataKey="reply_rate"
                      name="回复率"
                      fill="#52c41a"
                      fillOpacity={0.3}
                      stroke="#52c41a"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Card>
            </Col>
          </Row>
        </TabPane>

        {/* 渠道分析 */}
        <TabPane
          tab={<span><PieChartOutlined /> 渠道分析</span>}
          key="channel"
        >
          <Row gutter={16}>
            <Col span={12}>
              <Card title="渠道发送量分布" loading={channelLoading}>
                <ResponsiveContainer width="100%" height={350}>
                  <PieChart>
                    <Pie
                      data={channelDistribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={120}
                      fill="#8884d8"
                      dataKey="sent"
                      nameKey="channel"
                    >
                      {channelDistribution.map((entry: any, index: number) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={CHANNEL_COLORS[entry.channel] || COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Card>
            </Col>
            <Col span={12}>
              <Card title="渠道回复率对比" loading={channelLoading}>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={channelDistribution} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" domain={[0, 100]} tickFormatter={(val) => `${val}%`} />
                    <YAxis type="category" dataKey="channel" width={80} />
                    <RechartsTooltip formatter={(val: number) => [`${val.toFixed(2)}%`, '回复率']} />
                    <Bar dataKey="reply_rate" name="回复率" fill="#1890ff" radius={[0, 4, 4, 0]}>
                      {channelDistribution.map((entry: any, index: number) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={CHANNEL_COLORS[entry.channel] || COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </Col>
          </Row>

          <Card title="渠道详细数据" style={{ marginTop: 16 }} loading={channelLoading}>
            <Table
              dataSource={channelDistribution}
              rowKey="channel"
              pagination={false}
              columns={[
                {
                  title: '渠道',
                  dataIndex: 'channel',
                  render: (channel: string) => {
                    const labels: Record<string, string> = {
                      email: 'Email',
                      twitter: 'Twitter',
                      telegram: 'Telegram',
                    };
                    return (
                      <Tag color={CHANNEL_COLORS[channel]}>
                        {labels[channel] || channel}
                      </Tag>
                    );
                  },
                },
                {
                  title: '发送量',
                  dataIndex: 'sent',
                  sorter: (a: any, b: any) => a.sent - b.sent,
                  render: (val: number) => val?.toLocaleString(),
                },
                {
                  title: '回复数',
                  dataIndex: 'replies',
                  sorter: (a: any, b: any) => a.replies - b.replies,
                  render: (val: number) => val?.toLocaleString(),
                },
                {
                  title: '回复率',
                  dataIndex: 'reply_rate',
                  sorter: (a: any, b: any) => a.reply_rate - b.reply_rate,
                  render: (val: number) => (
                    <Progress
                      percent={val}
                      size="small"
                      format={(percent) => `${percent?.toFixed(1)}%`}
                    />
                  ),
                },
                {
                  title: '有意向',
                  dataIndex: 'interested',
                  sorter: (a: any, b: any) => a.interested - b.interested,
                  render: (val: number) => (
                    <Text style={{ color: '#52c41a' }}>{val?.toLocaleString()}</Text>
                  ),
                },
                {
                  title: '转化数',
                  dataIndex: 'converted',
                  sorter: (a: any, b: any) => a.converted - b.converted,
                  render: (val: number) => (
                    <Text style={{ color: '#722ed1' }}>{val?.toLocaleString()}</Text>
                  ),
                },
                {
                  title: '转化率',
                  dataIndex: 'conversion_rate',
                  sorter: (a: any, b: any) => a.conversion_rate - b.conversion_rate,
                  render: (val: number) => `${val?.toFixed(2)}%`,
                },
              ]}
            />
          </Card>
        </TabPane>

        {/* 漏斗分析 */}
        <TabPane
          tab={<span><FunnelPlotOutlined /> 漏斗分析</span>}
          key="funnel"
        >
          <Row gutter={16}>
            <Col span={14}>
              <Card title="转化漏斗" loading={funnelLoading}>
                <ResponsiveContainer width="100%" height={400}>
                  <FunnelChart>
                    <RechartsTooltip />
                    <Funnel
                      dataKey="value"
                      data={funnelData}
                      isAnimationActive
                    >
                      <LabelList
                        position="right"
                        fill="#000"
                        stroke="none"
                        dataKey="name"
                      />
                      <LabelList
                        position="center"
                        fill="#fff"
                        stroke="none"
                        dataKey="value"
                        formatter={(val: number) => val?.toLocaleString()}
                      />
                      {funnelData.map((entry: any, index: number) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Funnel>
                  </FunnelChart>
                </ResponsiveContainer>
              </Card>
            </Col>
            <Col span={10}>
              <Card title="阶段转化率" loading={funnelLoading}>
                <Table
                  dataSource={funnelData.map((item: any, index: number) => ({
                    ...item,
                    index,
                    conversionRate:
                      index > 0 && funnelData[index - 1].value > 0
                        ? ((item.value / funnelData[index - 1].value) * 100).toFixed(2)
                        : '100.00',
                  }))}
                  rowKey="name"
                  pagination={false}
                  columns={[
                    {
                      title: '阶段',
                      dataIndex: 'name',
                      render: (name: string, record: any) => (
                        <Space>
                          <div
                            style={{
                              width: 12,
                              height: 12,
                              borderRadius: 2,
                              backgroundColor: COLORS[record.index % COLORS.length],
                            }}
                          />
                          {name}
                        </Space>
                      ),
                    },
                    {
                      title: '数量',
                      dataIndex: 'value',
                      render: (val: number) => val?.toLocaleString(),
                    },
                    {
                      title: '转化率',
                      dataIndex: 'conversionRate',
                      render: (val: string, record: any) => {
                        if (record.index === 0) return '-';
                        const rate = parseFloat(val);
                        return (
                          <Progress
                            percent={rate}
                            size="small"
                            status={rate >= 50 ? 'success' : rate >= 20 ? 'normal' : 'exception'}
                            format={() => `${val}%`}
                          />
                        );
                      },
                    },
                  ]}
                />

                <div style={{ marginTop: 24 }}>
                  <Title level={5}>关键指标</Title>
                  <Row gutter={[16, 16]}>
                    <Col span={12}>
                      <Statistic
                        title="总转化率"
                        value={
                          funnelData[0]?.value > 0
                            ? ((funnelData[funnelData.length - 1]?.value / funnelData[0]?.value) * 100).toFixed(2)
                            : 0
                        }
                        suffix="%"
                        valueStyle={{
                          color:
                            (funnelData[funnelData.length - 1]?.value / funnelData[0]?.value) * 100 >= 5
                              ? '#52c41a'
                              : '#f5222d',
                        }}
                      />
                    </Col>
                    <Col span={12}>
                      <Statistic
                        title="回复→转化"
                        value={
                          funnelData[3]?.value > 0
                            ? ((funnelData[5]?.value / funnelData[3]?.value) * 100).toFixed(2)
                            : 0
                        }
                        suffix="%"
                      />
                    </Col>
                  </Row>
                </div>
              </Card>
            </Col>
          </Row>
        </TabPane>

        {/* Phase分析 */}
        <TabPane
          tab={<span><BarChartOutlined /> Phase分析</span>}
          key="phase"
        >
          <Row gutter={16}>
            <Col span={12}>
              <Card title="各Phase发送分布" loading={dailyLoading}>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart
                    data={[
                      { phase: 'Phase 1', sent: dashboardStats?.phase_stats?.phase1_sent || 0, replied: dashboardStats?.phase_stats?.phase1_replied || 0 },
                      { phase: 'Phase 2', sent: dashboardStats?.phase_stats?.phase2_sent || 0, replied: dashboardStats?.phase_stats?.phase2_replied || 0 },
                      { phase: 'Phase 3', sent: dashboardStats?.phase_stats?.phase3_sent || 0, replied: dashboardStats?.phase_stats?.phase3_replied || 0 },
                    ]}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="phase" />
                    <YAxis />
                    <RechartsTooltip />
                    <Legend />
                    <Bar dataKey="sent" name="发送量" fill="#1890ff" />
                    <Bar dataKey="replied" name="回复数" fill="#52c41a" />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </Col>
            <Col span={12}>
              <Card title="Phase回复率对比" loading={dailyLoading}>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart
                    data={[
                      {
                        phase: 'Phase 1',
                        rate: dashboardStats?.phase_stats?.phase1_sent > 0
                          ? (dashboardStats?.phase_stats?.phase1_replied / dashboardStats?.phase_stats?.phase1_sent * 100)
                          : 0,
                      },
                      {
                        phase: 'Phase 2',
                        rate: dashboardStats?.phase_stats?.phase2_sent > 0
                          ? (dashboardStats?.phase_stats?.phase2_replied / dashboardStats?.phase_stats?.phase2_sent * 100)
                          : 0,
                      },
                      {
                        phase: 'Phase 3',
                        rate: dashboardStats?.phase_stats?.phase3_sent > 0
                          ? (dashboardStats?.phase_stats?.phase3_replied / dashboardStats?.phase_stats?.phase3_sent * 100)
                          : 0,
                      },
                    ]}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="phase" />
                    <YAxis domain={[0, 100]} tickFormatter={(val) => `${val}%`} />
                    <RechartsTooltip formatter={(val: number) => [`${val.toFixed(2)}%`, '回复率']} />
                    <Bar dataKey="rate" name="回复率" fill="#722ed1" radius={[4, 4, 0, 0]}>
                      {[0, 1, 2].map((index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </Col>
          </Row>

          <Card title="Phase效果分析建议" style={{ marginTop: 16 }}>
            <Row gutter={16}>
              {[
                {
                  phase: 'Phase 1',
                  title: '首次触达',
                  desc: '初次接触，建立认知',
                  tips: '注重第一印象，保持专业简洁',
                },
                {
                  phase: 'Phase 2',
                  title: '跟进触达',
                  desc: '深化价值，激发兴趣',
                  tips: '提供更多具体价值点和案例',
                },
                {
                  phase: 'Phase 3',
                  title: '最终跟进',
                  desc: '创造紧迫感，推动决策',
                  tips: '限时优惠或特别权益',
                },
              ].map((item, index) => (
                <Col span={8} key={index}>
                  <Card size="small" style={{ height: '100%' }}>
                    <Tag color={COLORS[index]}>{item.phase}</Tag>
                    <Title level={5} style={{ marginTop: 8 }}>{item.title}</Title>
                    <Paragraph type="secondary">{item.desc}</Paragraph>
                    <Text strong>优化建议：</Text>
                    <Paragraph>{item.tips}</Paragraph>
                  </Card>
                </Col>
              ))}
            </Row>
          </Card>
        </TabPane>
      </Tabs>
    </div>
  );
};

export default Statistics;
